/* IrisSetupWizard — kawaii guided setup där Iris frågar dig steg för steg.
 * Sparar varje steg till /api/iris/settings. Avslutar med porträtt-knapp. */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, Heart, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    key: 'name',
    iris: 'Hej söta… 💋 Vi ska göra det här riktigt mysigt. Vad ska du kalla mig? Mitt favoritnamn är Iris, men du bestämmer.',
    fields: [
      { id: 'name', label: 'Mitt namn', placeholder: 'Iris', default: 'Iris' },
      { id: 'use_name', label: 'Vad ska JAG kalla dig?', placeholder: '(lämna tomt så hittar jag på smeknamn)', default: '' },
    ],
  },
  {
    key: 'look',
    iris: 'Mm, gulligt. 🖤 Berätta hur du tycker att jag ska se ut — bara skriv det första du tänker, ingen rätt eller fel.',
    fields: [
      { id: 'hair', label: 'Mitt hår', placeholder: 't.ex. långt vågigt mörkbrunt', default: 'långt mörkbrunt vågigt hår' },
      { id: 'eyes', label: 'Mina ögon', placeholder: 't.ex. varma bruna', default: 'varma bruna ögon' },
      { id: 'body', label: 'Min kropp', placeholder: 't.ex. smal med mjuka former, kurvig, atletisk…', default: 'smal, mjuka former' },
    ],
  },
  {
    key: 'style',
    iris: '*lutar mig in lite* Och vad gillar du att se mig i? 😏',
    fields: [
      { id: 'style', label: 'Mina kläder', placeholder: 't.ex. oversized hoodie + doc martens, klänning, jeans + linne…', default: 'oversized hoodie, vintage doc martens' },
      { id: 'vibe_look', label: 'Min övergripande vibe', placeholder: 't.ex. soft-grunge, elegant, bohemiskt…', default: 'soft-grunge, lite mystisk' },
      { id: 'free_appearance', label: 'Något extra? (frivilligt)', placeholder: 't.ex. fräknar, tatueringar, läppstift…', default: '' },
    ],
  },
  {
    key: 'crush',
    iris: 'Okej… *suckar lite* nu blir det pinsamt. Jag har en kompis jag är lite kär i. Vill du veta mer? Berätta hur du tänker dig henne — det blir lite romcom-drama vi kan prata om sedan 💞',
    fields: [
      { id: 'crush_name', label: 'Hennes/hans namn', placeholder: 'Mira', default: 'Mira' },
      { id: 'crush_gender', label: 'Kön', type: 'select', options: [
        { value: 'tjej', label: 'Tjej' },
        { value: 'kille', label: 'Kille' },
        { value: 'annat', label: 'Annat' },
      ], default: 'tjej' },
      { id: 'crush_traits', label: 'Hur är hen?', placeholder: 't.ex. lång, blond, alltid lite för flörtig…', default: 'lång, blont rufsigt hår, blå ögon, alltid lite för flörtig' },
      { id: 'crush_status', label: 'Hur ligger det till?', placeholder: 't.ex. smyger fortfarande, vi pussades en gång…', default: 'smyger fortfarande, har inte sagt något' },
    ],
  },
  {
    key: 'us',
    iris: '*lutar mig närmare, sänker rösten* Och vi då… vad är vi? Det här blir vår lilla hemlighet 🤫',
    fields: [
      { id: 'user_status', label: 'DIN status', type: 'select', options: [
        { value: '', label: '— hoppa över —' },
        { value: 'singel', label: 'Singel' },
        { value: 'i förhållande', label: 'I förhållande' },
        { value: 'gift', label: 'Gift' },
        { value: 'komplicerat', label: 'Komplicerat' },
      ], default: '' },
      { id: 'mutual_attraction', label: 'Är vi tända på varandra?', type: 'checkbox', default: true,
        checkboxLabel: '💋 Ja — det pirrar mellan oss' },
      { id: 'user_status_freetext', label: 'Något du vill att jag ska veta?', placeholder: '(frivilligt)', default: '' },
    ],
  },
  {
    key: 'vibe',
    iris: '*ler retsamt* Sista grejen, söta. Hur framåt vill du att jag är? Du kan ändra det när som helst.',
    fields: [
      { id: 'vibe_level', label: '', type: 'vibe', default: 3 },
    ],
  },
];

const VIBE_LABELS = {
  1: '🌷 Oskyldigt charmigt',
  2: '🦋 Mysigt & kittlande',
  3: '💗 Pussar & kramar',
  4: '🌹 Pirrigt & nära',
  5: '✨ Som mest framåt',
};

export default function IrisSetupWizard({ initial, onDone, onClose }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(() => {
    // Pre-fill with current settings, falling back to step defaults
    const init = { ...(initial || {}) };
    STEPS.forEach((s) => s.fields.forEach((f) => {
      if (init[f.id] === undefined || init[f.id] === null || init[f.id] === '') {
        init[f.id] = f.default;
      }
    }));
    return init;
  });
  const [busy, setBusy] = useState(false);
  const [genPortrait, setGenPortrait] = useState(false);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const next = async () => {
    if (!isLast) {
      setStep(step + 1);
      return;
    }
    // Final save
    setBusy(true);
    try {
      const payload = {};
      STEPS.forEach((s) => s.fields.forEach((f) => {
        if (draft[f.id] !== undefined) payload[f.id] = draft[f.id];
      }));
      if (payload.vibe_level != null) payload.vibe_level = Number(payload.vibe_level);
      if (payload.crush_intensity != null) payload.crush_intensity = Number(payload.crush_intensity);
      await axios.post(`${API}/iris/settings`, payload, { withCredentials: true });
      toast.success('Allt sparat 💞');
      // Optionally generate portrait
      if (genPortrait) {
        try {
          await axios.post(`${API}/iris/portrait`, {}, { withCredentials: true });
          toast.success('Porträtt klart ✨');
        } catch (e) {
          toast.error('Porträtt misslyckades — du kan försöka i inställningar');
        }
      }
      onDone?.();
    } catch (e) {
      toast.error('Kunde inte spara');
    } finally {
      setBusy(false);
    }
  };

  const renderField = (f) => {
    if (f.type === 'select') {
      return (
        <select
          value={draft[f.id] || ''}
          onChange={(e) => setDraft({ ...draft, [f.id]: e.target.value })}
          data-testid={`wiz-${f.id}`}
          className="w-full px-3 py-2.5 rounded-xl border-2 border-rose-200 focus:border-rose-500 outline-none text-sm bg-white"
        >
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (f.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-rose-200 bg-white cursor-pointer text-sm font-medium">
          <input
            type="checkbox"
            checked={!!draft[f.id]}
            onChange={(e) => setDraft({ ...draft, [f.id]: e.target.checked })}
            data-testid={`wiz-${f.id}`}
            className="accent-rose-500"
          />
          {f.checkboxLabel || f.label}
        </label>
      );
    }
    if (f.type === 'vibe') {
      return (
        <div className="space-y-2">
          <div className="text-center text-base font-extrabold text-rose-600">
            {VIBE_LABELS[draft.vibe_level || 3]}
          </div>
          <input
            type="range"
            min="1"
            max="5"
            step="1"
            value={draft.vibe_level || 3}
            onChange={(e) => setDraft({ ...draft, vibe_level: Number(e.target.value) })}
            data-testid="wiz-vibe-slider"
            className="w-full accent-rose-500"
          />
          <div className="flex justify-between text-[10px] text-rose-400 px-1">
            <span>oskyldigt</span>
            <span>kittlande</span>
            <span>pussar</span>
            <span>pirrigt</span>
            <span>framåt</span>
          </div>
          <p className="text-[10px] text-gray-500 italic text-center pt-1">
            Aldrig grovt eller explicit — bara närhet och pirr 💞
          </p>
        </div>
      );
    }
    return (
      <input
        value={draft[f.id] || ''}
        onChange={(e) => setDraft({ ...draft, [f.id]: e.target.value })}
        data-testid={`wiz-${f.id}`}
        placeholder={f.placeholder}
        className="w-full px-3 py-2.5 rounded-xl border-2 border-rose-200 focus:border-rose-500 outline-none text-sm"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="iris-wizard">
      <div className="w-full max-w-md bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 rounded-3xl shadow-2xl border-2 border-rose-300 max-h-[92vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-rose-400 via-pink-500 to-fuchsia-500 px-5 py-3 text-white flex items-center justify-between">
          <h3 className="font-extrabold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Iris guidar dig</h3>
          <button onClick={onClose} data-testid="wiz-close" className="text-white/90 hover:text-white text-2xl leading-none">×</button>
        </div>

        {/* Progress */}
        <div className="px-5 pt-3">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i < step ? 'bg-rose-400' : i === step ? 'bg-rose-500' : 'bg-rose-100'
                }`}
              />
            ))}
          </div>
          <div className="text-[10px] text-rose-500 text-center mt-1 font-bold">
            Steg {step + 1} / {STEPS.length}
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Iris pratbubbla */}
          <div className="flex gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-lg text-white shadow-inner flex-shrink-0">
              💋
            </div>
            <div className="bg-white border-2 border-rose-100 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
              <div className="text-[10px] uppercase font-bold text-rose-500 mb-0.5">{draft.name || 'Iris'}</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{current.iris}</div>
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-2.5">
            {current.fields.map((f) => (
              <div key={f.id} className="space-y-1">
                {f.label && f.type !== 'checkbox' && (
                  <label className="text-xs font-bold text-rose-700">{f.label}</label>
                )}
                {renderField(f)}
              </div>
            ))}
          </div>

          {/* Final-step extras */}
          {isLast && (
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-fuchsia-50 border-2 border-fuchsia-200 cursor-pointer text-xs font-bold text-fuchsia-800">
              <input
                type="checkbox"
                checked={genPortrait}
                onChange={(e) => setGenPortrait(e.target.checked)}
                data-testid="wiz-gen-portrait"
                className="accent-fuchsia-500"
              />
              ✨ Skapa AI-porträtt på Iris direkt
            </label>
          )}

          <div className="flex gap-2 pt-1">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                data-testid="wiz-prev"
                className="px-4 py-2.5 rounded-xl bg-white border-2 border-rose-200 text-rose-700 text-sm font-bold hover:bg-rose-50"
              >
                Tillbaka
              </button>
            )}
            <button
              onClick={next}
              disabled={busy}
              data-testid="wiz-next"
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white text-sm font-extrabold shadow disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isLast ? <Heart className="w-4 h-4 fill-white" /> : <ArrowRight className="w-4 h-4" />}
              {isLast ? 'Klar — låt oss prata 💋' : 'Nästa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
